import { useState, useMemo, useRef, useEffect, Fragment } from "react";
import { TalentPipeline } from "@/components/TalentPipeline";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Users, CalendarDays, Settings2, ClipboardList, UploadCloud, Loader2, Plus, Trash2,
  Sparkles, ArrowRight, FileSpreadsheet, AlertCircle, ReceiptText, CheckCircle2, XCircle,
  ThumbsUp, Download, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import Performance from "@/pages/Performance";
import axios from "axios";
import {
  listEmployees, listHolidays, createHoliday, deleteHoliday,
  uploadAttendance, listAttendance, listAttendanceBatches, exportAttendanceXlsx,
  generateSalary, listSalaryRecords,
  listSalaryAdvanceSummaries, uploadSalaryAdvanceXlsx, getSalaryAdvanceUpload, clearSalaryAdvanceUpload, salaryExportUrl,
} from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const listLeaves = async (scope = "all") => (await axios.get(`${API}/leave/requests`, { params: { scope }, withCredentials: true })).data;
const leaveAction = async (lrId, action, note = null) => (await axios.post(`${API}/leave/${lrId}/${action}`, { note }, { withCredentials: true })).data;

const currentFiscalYear = () => {
  const d = new Date();
  const y = d.getFullYear();
  const startYear = d.getMonth() >= 3 ? y : y - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};
const fyOptions = () => {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return Array.from({ length: 4 }, (_, i) => `${startYear - i}-${String(startYear - i + 1).slice(-2)}`);
};
const monthOptions = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i += 1) {
    const y = d.getFullYear();
    const m = d.getMonth() - i;
    const dd = new Date(y, m, 1);
    out.push(`${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};
const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });


const TABS = [
  { key: "employees", label: "Employees", icon: Users, module: "hr.employees" },
  { key: "attendance", label: "Attendance & Salary Advance", icon: ClipboardList, module: "hr.attendance" },
  { key: "leave", label: "Leave Requests", icon: CalendarDays, module: "hr.leave_requests" },
  { key: "holidays", label: "Holidays", icon: CalendarDays, module: "hr.holidays" },
  { key: "rules", label: "Salary Rules", icon: Settings2, module: "hr.salary_rules" },
  { key: "salary", label: "Salary Calculator", icon: ReceiptText, module: "hr.salary" },
  { key: "performance", label: "Performance", icon: ThumbsUp, module: "performance" },
  { key: "talent", label: "Talent Pipeline", icon: Users, module: "hr.talent_pipeline" },
];

// -----------------------------------------------------------------------------
// EmployeesTab
// -----------------------------------------------------------------------------
const EmployeesTab = () => {
  const { data: employees = [], isLoading } = useQuery({ queryKey: ["masters", "employee"], queryFn: listEmployees });
  const active = useMemo(
    () => employees.filter((e) => e.active !== false && (e.status || "active") === "active"),
    [employees],
  );

  const [sortBy, setSortBy] = useState("code");    // code | doh
  const [sortDir, setSortDir] = useState("asc");
  const [esiFilter, setEsiFilter] = useState("all"); // all | y | n
  const [pfFilter, setPfFilter] = useState("all");

  const empDoh = (e) => e.meta?.date_of_hire || e.meta?.join_date || "";
  const boolYn = (v) => (v ? "Y" : "N");

  const filtered = useMemo(() => {
    return active.filter((e) => {
      const esi = !!e.meta?.esi_eligible;
      const pf  = !!e.meta?.pf_eligible;
      if (esiFilter === "y" && !esi) return false;
      if (esiFilter === "n" && esi)  return false;
      if (pfFilter === "y" && !pf)   return false;
      if (pfFilter === "n" && pf)    return false;
      return true;
    });
  }, [active, esiFilter, pfFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const key = (e) => (sortBy === "doh" ? empDoh(e) : (e.code || "").toString());
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      // Empties always at the bottom regardless of direction
      if (!ka && !kb) return 0;
      if (!ka) return 1;
      if (!kb) return -1;
      if (ka < kb) return -1 * dir;
      if (ka > kb) return  1 * dir;
      return 0;
    });
  }, [filtered, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };
  const arrow = (col) => (sortBy === col ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  return (
    <div data-testid="hr-employees-tab" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-600">{sorted.length} of {active.length} active employees. Add/edit in the Masters module.</p>
        <Link to="/masters" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 border border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-sm px-3 py-2" data-testid="link-masters-employees">
          Manage in Masters <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-slate-50/70 border border-slate-200 rounded-sm p-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Eligible for ESI</label>
          <select value={esiFilter} onChange={(e) => setEsiFilter(e.target.value)}
            data-testid="emp-filter-esi"
            className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white">
            <option value="all">All</option>
            <option value="y">Yes</option>
            <option value="n">No</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Eligible for PF</label>
          <select value={pfFilter} onChange={(e) => setPfFilter(e.target.value)}
            data-testid="emp-filter-pf"
            className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white">
            <option value="all">All</option>
            <option value="y">Yes</option>
            <option value="n">No</option>
          </select>
        </div>
        <Button size="sm" variant="outline" data-testid="emp-filter-reset"
          onClick={() => { setEsiFilter("all"); setPfFilter("all"); setSortBy("code"); setSortDir("asc"); }}>
          Reset
        </Button>
      </div>

      {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <div className="overflow-x-auto border border-slate-200 rounded-sm">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-900 text-white text-[10px] tracking-[0.2em] uppercase">
              <tr>
                <th className="px-3 py-2 text-left cursor-pointer" onClick={() => toggleSort("code")} data-testid="emp-sort-code">
                  Emp ID{arrow("code")}
                </th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Department</th>
                <th className="px-3 py-2 text-right">Monthly Salary (₹)</th>
                <th className="px-3 py-2 text-center">Eligible for ESI</th>
                <th className="px-3 py-2 text-center">Eligible for PF</th>
                <th className="px-3 py-2 text-left cursor-pointer" onClick={() => toggleSort("doh")} data-testid="emp-sort-doh">
                  Date of Hiring{arrow("doh")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono">{e.code || "—"}</td>
                  <td className="px-3 py-2 font-semibold">{e.name}</td>
                  <td className="px-3 py-2">{e.meta?.department || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(e.meta?.monthly_basic_salary)}</td>
                  <td className={`px-3 py-2 text-center font-bold ${e.meta?.esi_eligible ? "text-emerald-700" : "text-slate-400"}`} data-testid={`emp-esi-${e.id}`}>
                    {boolYn(e.meta?.esi_eligible)}
                  </td>
                  <td className={`px-3 py-2 text-center font-bold ${e.meta?.pf_eligible ? "text-emerald-700" : "text-slate-400"}`} data-testid={`emp-pf-${e.id}`}>
                    {boolYn(e.meta?.pf_eligible)}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-600">{empDoh(e) || "—"}</td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No employees match the filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// HolidaysTab
// -----------------------------------------------------------------------------
const HolidaysTab = () => {
  const qc = useQueryClient();
  const [fy, setFy] = useState(currentFiscalYear());
  const [name, setName] = useState("");
  const [dt, setDt] = useState("");
  const { data: holidays = [], isLoading } = useQuery({ queryKey: ["hr-holidays", fy], queryFn: () => listHolidays(fy) });
  const addMut = useMutation({
    mutationFn: createHoliday,
    onSuccess: () => { toast.success("Holiday added"); setName(""); setDt(""); qc.invalidateQueries({ queryKey: ["hr-holidays"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Add failed"),
  });
  const delMut = useMutation({
    mutationFn: deleteHoliday,
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["hr-holidays"] }); },
  });
  const submit = () => {
    if (!name.trim() || !dt) { toast.error("Name and date required"); return; }
    addMut.mutate({ name: name.trim(), date: dt, fiscal_year: fy, notes: "" });
  };
  return (
    <div data-testid="hr-holidays-tab" className="space-y-4">
      <div className="flex items-end gap-2 flex-wrap bg-white border border-slate-200 rounded-sm p-4">
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Fiscal Year</label>
          <select data-testid="fy-select" value={fy} onChange={(e) => setFy(e.target.value)} className="mt-0.5 block h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono bg-white">
            {fyOptions().map((f) => <option key={f} value={f}>FY {f}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Holiday name</label>
          <Input data-testid="holiday-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Republic Day" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Date</label>
          <Input data-testid="holiday-date" type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="font-mono" />
        </div>
        <Button onClick={submit} data-testid="add-holiday-btn" disabled={addMut.isPending} className="bg-slate-900 hover:bg-slate-800 text-white h-10">
          {addMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add</>}
        </Button>
      </div>
      <div className="border border-slate-200 rounded-sm bg-white">
        {isLoading ? <div className="p-6"><Loader2 className="h-5 w-5 animate-spin" /></div> : holidays.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 text-center">No holidays declared for FY {fy}.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[10px] tracking-[0.2em] uppercase text-slate-500">
              <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.id} data-testid={`holiday-row-${h.id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono">{h.date}</td>
                  <td className="px-3 py-2 font-semibold">{h.name}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => delMut.mutate(h.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`delete-holiday-${h.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// SalaryRulesTab — read-only rulebook (Task 1). No edit UI.
// -----------------------------------------------------------------------------
const SalaryRulesTab = () => {
  return (
    <div data-testid="hr-rules-tab" className="max-w-4xl space-y-5">
      <div className="border-l-4 border-slate-900 pl-3">
        <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Reference</p>
        <h3 className="font-heading text-2xl font-bold text-slate-900">Salary calculation rules</h3>
        <p className="text-sm text-slate-600 mt-1">
          These rules are hard-coded in the salary engine. Toggle each employee's
          eligibility in <span className="font-mono">Masters → Employees</span>.
        </p>
      </div>

      {/* Statutory (offline) note */}
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-[12px] text-amber-900 space-y-1" data-testid="rule-card-statutory">
        <p className="font-semibold">Statutory deductions (ESI &amp; PF) — handled offline</p>
        <p>
          ESI and PF are <b>no longer computed inside the ERP</b>. The salary sheet only shows
          each employee's eligibility (Y/N) so the accountant can process them offline.
          The eligibility toggles on the Employee master stay meaningful for reporting.
        </p>
      </div>

      {/* Overtime + attendance note */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-[12px] text-slate-700 space-y-1" data-testid="rule-card-notes">
        <p className="font-semibold text-slate-800">Attendance &amp; overtime</p>
        <ul className="list-disc pl-6 space-y-0.5">
          <li>Any punched-in day is treated as a <b>full present day</b>, unless the half-day rule below applies.</li>
          <li><b>Half day</b> (0.5) is credited when either:
            <ul className="list-disc pl-6 space-y-0.5 mt-0.5">
              <li>the employee <b>punches out before 13:00 (1 pm)</b>, or</li>
              <li>the employee <b>punches in after 13:00 (1 pm)</b>.</li>
            </ul>
            The remaining 0.5 of that day is rolled into the employee's <b>LWP Days</b> (Leave Without Pay), so the salary sheet only shows a single LWP column and no separate "½ day" column.
          </li>
          <li><b>Overtime</b> is credited only to employees whose master has <span className="font-mono">OT eligible</span> ticked, for the minutes after the shift-end.</li>
          <li>Sundays and company holidays count as worked/paid days for salary proration.</li>
          <li><b>Paid Sunday (Sandwich Rule)</b> — a Sunday is <b>paid</b> when:
            <ol className="list-decimal pl-6 space-y-0.5 mt-0.5">
              <li>The preceding Saturday is <b>not absent</b>, and</li>
              <li>The following Monday is <b>not absent</b>, and</li>
              <li>At least one of Saturday or Monday is <b>actually worked</b> (present or half-day).</li>
            </ol>
            <div className="mt-1 text-[11px] text-slate-500">
              A single company holiday on one side is fine as long as the other side is worked.
              A holiday on <i>both</i> sides (no work at all) makes the Sunday unpaid — it's a "Sandwich Sunday".
            </div>
            <div className="overflow-x-auto mt-2">
              <table className="text-[11px] border border-slate-200">
                <thead className="bg-slate-100 text-slate-700 tracking-wider">
                  <tr>
                    <th className="px-2 py-1 text-left">Saturday</th>
                    <th className="px-2 py-1 text-left">Monday</th>
                    <th className="px-2 py-1 text-left">Sunday</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {[
                    ["Holiday",  "Present",  "PAID"],
                    ["Present",  "Holiday",  "PAID"],
                    ["Absent",   "Holiday",  "Unpaid"],
                    ["Holiday",  "Absent",   "Unpaid"],
                    ["Present",  "Absent",   "Unpaid"],
                    ["Absent",   "Present",  "Unpaid"],
                    ["Absent",   "Absent",   "Unpaid"],
                  ].map(([s,m,r]) => (
                    <tr key={`${s}-${m}`} className="border-t border-slate-100">
                      <td className="px-2 py-1">{s}</td>
                      <td className="px-2 py-1">{m}</td>
                      <td className={`px-2 py-1 font-bold ${r === "PAID" ? "text-emerald-700" : "text-red-700"}`}>{r}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// AttendanceTab
// -----------------------------------------------------------------------------
const AttendanceTab = () => {
  const { hasModule } = useAuth();
  const canUpload = hasModule("hr.attendance_upload");
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(monthOptions()[0]);
  const [drilldown, setDrilldown] = useState(null);   // employee_id (or raw_name key) to show
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);
  const { data: batches = [] } = useQuery({ queryKey: ["hr-batches"], queryFn: listAttendanceBatches });
  const { data: records = [], isLoading } = useQuery({ queryKey: ["hr-attendance", month], queryFn: () => listAttendance({ month }) });
  const uploadMut = useMutation({
    mutationFn: (fd) => uploadAttendance(fd),
    onSuccess: (data) => {
      toast.success(`Uploaded ${data.matched_rows} rows${data.unmatched_rows ? ` · ${data.unmatched_rows} unmatched` : ""}`);
      if (data.unmatched_names?.length) toast.warning(`Unmatched: ${data.unmatched_names.join(", ")}`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["hr-batches"] });
      qc.invalidateQueries({ queryKey: ["hr-attendance"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });
  const onUpload = () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (month) fd.append("month", month);  // tag the batch with the operator-selected month
    uploadMut.mutate(fd);
  };
  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAttendanceXlsx({ month });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };
  const fmtHrs = (h) => (typeof h === "number" ? h.toFixed(2) : "—");

  // Group records by employee for the drilldown dialog
  const empRecords = useMemo(() => {
    if (!drilldown) return [];
    return records.filter((r) => (r.employee_id || r.raw_name) === drilldown.key);
  }, [drilldown, records]);

  return (
    <div data-testid="hr-attendance-tab" className="space-y-4">
      <div className="border-t-4 border-slate-900 bg-white rounded-sm" data-testid="attendance-upload-card">
        <div className="border-b border-slate-100 px-4 py-3">
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Section 1</p>
          <h3 className="font-heading text-lg font-bold text-slate-900">Attendance Excel · {month}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            <b className="text-slate-900">Upload attendance excel file.</b>
          </p>
        </div>
        {canUpload ? (
          <div className="p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[280px]">
              <label htmlFor="attendance-file" className="mt-1 inline-flex items-center gap-2 border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 px-3 h-10 rounded-sm cursor-pointer text-sm">
                <UploadCloud className="h-4 w-4" /> {file ? file.name : "Choose file (.xlsx / .xls)"}
              </label>
              <input id="attendance-file" ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="attendance-file-input" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">For month</label>
              <select
                data-testid="attendance-upload-month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="mt-0.5 block h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono bg-white"
              >
                {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <Button onClick={onUpload} disabled={!file || uploadMut.isPending} className="bg-slate-900 hover:bg-slate-800 text-white h-10" data-testid="attendance-upload-btn">
              {uploadMut.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</> : <><FileSpreadsheet className="h-4 w-4 mr-2" /> Upload</>}
            </Button>
          </div>
        ) : (
          <div className="mx-4 my-4 border border-dashed border-slate-300 rounded-sm bg-slate-50 p-3 text-xs text-slate-500" data-testid="attendance-upload-gated">
            Only users with the <b>Upload Attendance</b> ERP module can import biometric files. Ask the Director to grant it in ERP Users.
          </div>
        )}
        {batches.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-3">
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-2">Recent uploads</p>
            <ul className="space-y-1 text-xs text-slate-600 font-mono">
              {batches.slice(0, 5).map((b) => (
                <li key={b.id} className="flex items-center gap-3 flex-wrap">
                  <span className="text-slate-400">{b.uploaded_at?.slice(0, 10)}</span>
                  <span className="font-semibold text-slate-800">{b.filename}</span>
                  {b.month && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700">{b.month}</span>}
                  <span>· {b.period_start} → {b.period_end}</span>
                  <span>· {b.row_count} rows</span>
                  {b.source_format === "pivoted" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">Biometric</span>
                  )}
                  {b.unmatched_count > 0 && (
                    <span className="text-amber-700 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {b.unmatched_count} unmatched</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Month</label>
        <select data-testid="attendance-month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 px-2 border border-slate-300 rounded-sm text-sm font-mono bg-white">
          {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <span className="text-xs text-slate-500">{records.length} rows</span>
        <div className="ml-auto">
          <Button size="sm" variant="outline" onClick={onExport} disabled={exporting || records.length === 0} data-testid="attendance-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Export Excel
          </Button>
        </div>
      </div>
      <AttendanceGrid
        month={month}
        records={records}
        isLoading={isLoading}
        onEmployeeClick={(info) => setDrilldown(info)}
      />
      <SalaryAdvanceUploadCard month={month} />
      {drilldown && (
        <EmployeeMonthDialog
          month={month}
          employee={drilldown}
          records={empRecords}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// AttendanceGrid — Section-2 chrome around a pivoted Employee × Day table.
// Each employee gets a 4-row block: Status / InTime / OutTime / Duration.
// Duration is computed from (OutTime − InTime); the duration column from the
// uploaded Excel is intentionally ignored.
// -----------------------------------------------------------------------------
const DOW_SHORT = ["S", "M", "T", "W", "Th", "F", "St"]; // JS getDay() → letter
const _toMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(mm)) return null;
  return h * 60 + mm;
};
const _durationHHMM = (inT, outT) => {
  const a = _toMinutes(inT);
  const b = _toMinutes(outT);
  if (a == null || b == null) return "00:00";
  let d = b - a;
  if (d <= 0) return "00:00";
  return `${Math.floor(d / 60)}:${String(d % 60).padStart(2, "0")}`;
};
const STATUS_CLS = {
  P:   "bg-emerald-50 text-emerald-800",
  A:   "bg-red-50 text-red-800",
  H:   "bg-blue-50 text-blue-800",
  WO:  "bg-slate-100 text-slate-600",
  WOP: "bg-amber-50 text-amber-800",
  L:   "bg-amber-50 text-amber-800",
};

const AttendanceGrid = ({ month, records, isLoading, onEmployeeClick }) => {
  const { year, mon } = useMemo(() => {
    const [y, m] = (month || "").split("-").map((n) => parseInt(n, 10));
    return { year: y, mon: m };
  }, [month]);

  const days = useMemo(() => {
    if (!year || !mon) return [];
    const last = new Date(year, mon, 0).getDate();  // last day of month
    return Array.from({ length: last }, (_, i) => {
      const d = i + 1;
      const dow = DOW_SHORT[new Date(year, mon - 1, d).getDay()];
      return { d, dow, iso: `${year}-${String(mon).padStart(2, "0")}-${String(d).padStart(2, "0")}` };
    });
  }, [year, mon]);

  // Group records by employee key → { key, name, department, byDate: {ISO: record} }
  const employees = useMemo(() => {
    const map = new Map();
    for (const r of records || []) {
      const key = r.employee_id || r.raw_name || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: r.employee_name || r.raw_name || "—",
          department: r.department || "",
          ot_eligible: r.ot_eligible,
          byDate: {},
        });
      }
      map.get(key).byDate[r.date] = r;
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [records]);

  if (isLoading) return <div className="border border-slate-200 rounded-sm bg-white p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!records || records.length === 0) {
    return (
      <div className="border border-slate-200 rounded-sm bg-white p-8 text-center text-sm text-slate-500">
        No attendance records for {month}.
      </div>
    );
  }

  return (
    <div className="border-t-4 border-slate-900 bg-white rounded-sm" data-testid="attendance-grid-card">
      <div className="border-b border-slate-100 px-4 py-3">
        <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Attendance records · {month}</p>
        <h3 className="font-heading text-lg font-bold text-slate-900">Attendance records built by ERP using your attendance excel file</h3>
        <p className="text-[11px] text-amber-800 font-semibold mt-0.5">
          NOTE: DOUBLE CHECK THE VALUES IN THIS TABLE WITH ATTENDANCE CARDS. AI CAN MAKE MISTAKES.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-[11px] border-collapse" data-testid="attendance-grid-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="sticky left-0 z-10 bg-slate-50 text-left px-3 py-2 text-[10px] tracking-[0.15em] uppercase text-slate-600 font-semibold border-r border-slate-200 min-w-[180px]">
                Employee
              </th>
              <th className="px-2 py-2 text-left text-[10px] tracking-[0.15em] uppercase text-slate-600 font-semibold border-r border-slate-200">
                Days
              </th>
              {days.map((dd) => (
                <th key={dd.d} className="px-2 py-2 text-center font-mono text-slate-700 border-r border-slate-100 min-w-[52px]">
                  {dd.d} {dd.dow}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((emp, idx) => (
              <AttendanceEmployeeBlock
                key={emp.key}
                emp={emp}
                days={days}
                zebra={idx % 2 === 1}
                onClick={() => onEmployeeClick?.({ key: emp.key, name: emp.name, department: emp.department, ot_eligible: emp.ot_eligible })}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const AttendanceEmployeeBlock = ({ emp, days, zebra, onClick }) => {
  const rowBg = zebra ? "bg-amber-50/30" : "bg-white";
  // Left label cell spans all 4 sub-rows (status/in/out/duration).
  const leftCell = (
    <td
      rowSpan={4}
      onClick={onClick}
      className={`sticky left-0 z-[5] align-top px-3 py-2 border-r border-slate-200 border-t-2 border-t-slate-300 cursor-pointer hover:bg-slate-100 ${rowBg}`}
      data-testid={`att-emp-${emp.key}`}
    >
      <div className="font-semibold text-slate-900 underline decoration-dotted underline-offset-2">{emp.name}</div>
      <div className="text-[10px] text-slate-500">{emp.department || "—"}</div>
    </td>
  );

  const RowLabel = ({ children }) => (
    <td className={`px-2 py-1 text-[10px] tracking-wide uppercase text-slate-600 font-semibold border-r border-slate-200 ${rowBg}`}>
      {children}
    </td>
  );

  const statusCell = (dd) => {
    const r = emp.byDate[dd.iso];
    // Status is DERIVED here (we deliberately dropped the Excel's unreliable
    // status column at upload time). A record only counts as "present" if
    // the punch_in actually parses to a valid HH:MM — a stray non-time
    // string must not flip the day to P.
    const toMin = (hhmm) => {
      const m = String(hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
      return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : null;
    };
    const inMin = toMin(r?.punch_in);
    const outMin = toMin(r?.punch_out);
    let s;
    if (inMin != null) {
      const CUTOFF = 13 * 60;
      const half = (outMin != null && outMin < CUTOFF) || (inMin > CUTOFF);
      s = half ? "L" : "P";
    } else {
      s = dd.dow === "S" ? "WO" : "A";
    }
    const cls = STATUS_CLS[s] || "text-slate-400";
    return (
      <td key={`s-${dd.d}`} className={`px-1.5 py-1 text-center font-mono font-bold border-r border-slate-100 ${rowBg}`}>
        <span className={`inline-block px-1 py-0.5 rounded-sm ${cls}`}>{s || "—"}</span>
      </td>
    );
  };

  const inCell = (dd) => {
    const r = emp.byDate[dd.iso];
    return (
      <td key={`i-${dd.d}`} className={`px-1.5 py-1 text-center font-mono text-slate-700 border-r border-slate-100 ${rowBg}`}>
        {r?.punch_in || ""}
      </td>
    );
  };

  const outCell = (dd) => {
    const r = emp.byDate[dd.iso];
    return (
      <td key={`o-${dd.d}`} className={`px-1.5 py-1 text-center font-mono text-slate-700 border-r border-slate-100 ${rowBg}`}>
        {r?.punch_out || ""}
      </td>
    );
  };

  const durationCell = (dd) => {
    const r = emp.byDate[dd.iso];
    const dur = _durationHHMM(r?.punch_in, r?.punch_out);  // computed, not from Excel
    const zero = dur === "00:00";
    return (
      <td key={`d-${dd.d}`} className={`px-1.5 py-1 text-center font-mono border-r border-slate-100 ${rowBg} ${zero ? "text-slate-400" : "text-slate-900 font-semibold"}`}>
        {dur}
      </td>
    );
  };

  return (
    <>
      <tr className="border-t-2 border-slate-300">
        {leftCell}
        <RowLabel>Status</RowLabel>
        {days.map(statusCell)}
      </tr>
      <tr>
        <RowLabel>InTime</RowLabel>
        {days.map(inCell)}
      </tr>
      <tr>
        <RowLabel>OutTime</RowLabel>
        {days.map(outCell)}
      </tr>
      <tr>
        <RowLabel>Duration</RowLabel>
        {days.map(durationCell)}
      </tr>
    </>
  );
};


// ---- Salary Advance bulk upload card ---------------------------------------
function SalaryAdvanceUploadCard({ month: parentMonth }) {
  const { hasModule } = useAuth();
  const canUpload = hasModule("hr.salary_advance_upload");
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [expand, setExpand] = useState(false);
  const [month, setMonth] = useState(parentMonth);  // independent month selector for the advance upload
  useEffect(() => { if (parentMonth) setMonth(parentMonth); }, [parentMonth]);
  const fileRef = useRef(null);
  const { data: upload } = useQuery({
    queryKey: ["salary-advance-upload", month],
    queryFn: () => getSalaryAdvanceUpload(month),
  });
  const { data: summaries = [] } = useQuery({
    queryKey: ["salary-advance-summaries", month],
    queryFn: () => listSalaryAdvanceSummaries(month),
  });
  const uploadMut = useMutation({
    mutationFn: () => uploadSalaryAdvanceXlsx({ month, file }),
    onSuccess: (data) => {
      toast.success(`Advance file processed · ${data.matched_count} matched · ₹${data.total_to_deduct} to deduct`);
      if (data.unmatched_names?.length) toast.warning(`Unmatched: ${data.unmatched_names.join(", ")}`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["salary-advance-upload", month] });
      qc.invalidateQueries({ queryKey: ["salary-advance-summaries", month] });
      qc.invalidateQueries({ queryKey: ["hr-salary"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Advance upload failed"),
  });
  const clearMut = useMutation({
    mutationFn: () => clearSalaryAdvanceUpload(month),
    onSuccess: () => {
      toast.success("Advance file cleared for this month");
      qc.invalidateQueries({ queryKey: ["salary-advance-upload", month] });
      qc.invalidateQueries({ queryKey: ["salary-advance-summaries", month] });
      qc.invalidateQueries({ queryKey: ["hr-salary"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Clear failed"),
  });
  const hasUpload = upload && upload.id;
  return (
    <div className="border-t-4 border-slate-900 bg-white rounded-sm mt-6" data-testid="salary-advance-card">
      <div className="border-b border-slate-100 px-4 py-3">
        <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Section 2</p>
        <h3 className="font-heading text-lg font-bold text-slate-900">Salary Advance · {month}</h3>
        <p className="text-[11px] text-slate-500 mt-0.5">
          <b className="text-slate-900">Upload Salary Advance Excel.</b>
          {!hasUpload && <span className="ml-1 text-amber-800 font-semibold">No file uploaded — advances will be zero.</span>}
        </p>
      </div>
      {canUpload ? (
        <div className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[280px]">
            <label htmlFor="advance-file" className="mt-1 inline-flex items-center gap-2 border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 px-3 h-10 rounded-sm cursor-pointer text-sm" data-testid="advance-file-label">
              <UploadCloud className="h-4 w-4" /> {file ? file.name : "Choose .xlsx"}
            </label>
            <input id="advance-file" ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="advance-file-input" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">For month</label>
            <select
              data-testid="advance-upload-month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-0.5 block h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono bg-white"
            >
              {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <Button onClick={() => uploadMut.mutate()} disabled={!file || uploadMut.isPending} className="bg-slate-900 hover:bg-slate-800 text-white h-10" data-testid="advance-upload-btn">
            {uploadMut.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing…</> : <><FileSpreadsheet className="h-4 w-4 mr-2" /> Upload</>}
          </Button>
          {hasUpload && (
            <Button variant="outline" onClick={() => { if (window.confirm(`Clear the advance file for ${month}? Advances will be reset to zero.`)) clearMut.mutate(); }} disabled={clearMut.isPending} data-testid="advance-clear-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> Clear
            </Button>
          )}
        </div>
      ) : (
        <div className="mx-4 my-4 border border-dashed border-slate-300 rounded-sm bg-slate-50 p-3 text-xs text-slate-500" data-testid="advance-upload-gated">
          Only users with the <b>Upload Salary Advance</b> ERP module can import the tracker. Ask the Director to grant it in ERP Users.
        </div>
      )}
      {hasUpload && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-2">
          <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="advance-upload-meta">
            <span className="font-semibold text-slate-800">{upload.filename}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">Uploaded {upload.uploaded_at?.slice(0, 10)} by {upload.uploaded_by}</span>
            <span className="text-slate-400">·</span>
            <span>{upload.matched_count} matched / {upload.unmatched_count} unmatched</span>
            <span className="text-slate-400">·</span>
            <span className="font-mono font-bold text-slate-900">Total to deduct ₹ {upload.total_to_deduct?.toLocaleString?.("en-IN") ?? upload.total_to_deduct}</span>
            <button onClick={() => setExpand(!expand)} className="ml-auto text-slate-600 hover:text-slate-900 underline-offset-4 hover:underline">
              {expand ? "Hide breakdown" : "Show breakdown"}
            </button>
          </div>
          {upload.unmatched_names?.length > 0 && (
            <p className="text-[11px] text-amber-800" data-testid="advance-unmatched-list">
              <AlertCircle className="h-3 w-3 inline mr-1" />
              Unmatched employees (add them in Masters or fix spelling in the Excel): {upload.unmatched_names.join(", ")}
            </p>
          )}
          {expand && (
            <div className="border border-slate-200 rounded-sm overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-900 text-white text-[10px] tracking-[0.2em] uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Excel Emp Code</th>
                    <th className="px-3 py-2 text-left">Employee</th>
                    <th className="px-3 py-2 text-center">Matched</th>
                    <th className="px-3 py-2 text-right">Given This Month ₹</th>
                    <th className="px-3 py-2 text-right">To Deduct Next Payroll ₹</th>
                    <th className="px-3 py-2 text-right">Balance Outstanding ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.map((s) => (
                    <tr key={s.id} className="border-t border-slate-100" data-testid={`advance-summary-row-${s.emp_code_excel || s.raw_name}`}>
                      <td className="px-3 py-1.5 font-mono">{s.emp_code_excel}</td>
                      <td className="px-3 py-1.5">{s.employee_name || s.raw_name}</td>
                      <td className="px-3 py-1.5 text-center">{s.employee_id ? <span className="text-emerald-700 font-bold">Y</span> : <span className="text-red-700 font-bold">N</span>}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{Number(s.given_this_month || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold">{Number(s.to_deduct || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-slate-500">{Number(s.balance_outstanding || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Employee-of-the-month drilldown dialog ---------------------------------
function EmployeeMonthDialog({ month, employee, records, onClose }) {
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const byDate = useMemo(() => {
    const map = {};
    (records || []).forEach((r) => { map[r.date] = r; });
    return map;
  }, [records]);
  const days = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${String(y)}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    days.push({ iso, dom: d, rec: byDate[iso] });
  }
  const totals = useMemo(() => {
    const t = { P: 0, WO: 0, H: 0, L: 0, A: 0, other: 0, hours: 0, ot: 0 };
    (records || []).forEach((r) => {
      const s = r.status || "";
      if (t[s] != null) t[s] += 1; else t.other += 1;
      t.hours += Number(r.duration_hours || 0);
      t.ot += Number(r.ot_hours || 0);
    });
    return t;
  }, [records]);
  const fmt = (v) => (typeof v === "number" ? v.toFixed(2) : "—");
  const dayName = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" });
  const statusPill = (s) => {
    if (!s) return <span className="text-slate-300">—</span>;
    const cls =
      s === "P" ? "bg-emerald-100 text-emerald-800" :
      s === "WO" ? "bg-slate-100 text-slate-700" :
      s === "H" ? "bg-blue-100 text-blue-800" :
      s === "L" ? "bg-amber-100 text-amber-800" :
      s === "A" ? "bg-red-100 text-red-800" :
      "bg-slate-100 text-slate-600";
    return <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${cls}`}>{s}</span>;
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="att-drilldown-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white rounded-sm border border-slate-200 mt-8 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Attendance · {month}</p>
            <h3 className="font-heading text-lg font-bold text-slate-900">{employee.name}</h3>
            <p className="text-[11px] text-slate-500">{employee.department || "—"} · {employee.ot_eligible ? "OT eligible" : "Not eligible for OT"}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="att-drilldown-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-slate-100">
          {[
            ["Present",   totals.P, "text-emerald-800"],
            ["Weekly Off",totals.WO, "text-slate-700"],
            ["Holiday",   totals.H, "text-blue-800"],
            ["Leave",     totals.L, "text-amber-800"],
            ["Absent",    totals.A, "text-red-800"],
            ["Other",     totals.other, "text-slate-500"],
            ["Total Hours", fmt(totals.hours), "text-slate-900"],
            ["Total OT",    fmt(totals.ot), "text-emerald-700"],
          ].map(([label, val, cls]) => (
            <div key={label} className="border border-slate-200 rounded-sm bg-slate-50/60 px-2 py-1.5">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{label}</p>
              <p className={`font-mono text-sm font-bold ${cls}`}>{val}</p>
            </div>
          ))}
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-900 text-white text-[10px] tracking-[0.2em] uppercase sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Day</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-left">In</th>
                <th className="px-3 py-2 text-left">Out</th>
                <th className="px-3 py-2 text-right">Hours</th>
                <th className="px-3 py-2 text-right">OT Hours</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.iso} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-drill-row-${d.iso}`}>
                  <td className="px-3 py-1.5 font-mono">{d.iso}</td>
                  <td className="px-3 py-1.5 text-slate-500">{dayName(d.iso)}</td>
                  <td className="px-3 py-1.5 text-center">{statusPill(d.rec?.status)}</td>
                  <td className="px-3 py-1.5 font-mono">{d.rec?.punch_in || "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{d.rec?.punch_out || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{d.rec?.duration_hours != null ? fmt(d.rec.duration_hours) : "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    {d.rec?.ot_eligible === false ? (
                      <span className="text-slate-400">—</span>
                    ) : d.rec ? (
                      <span className={Number(d.rec.ot_hours || 0) > 0 ? "text-emerald-700 font-semibold" : "text-slate-500"}>
                        {fmt(d.rec.ot_hours || 0)}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// OtBreakdownRow — tiny per-day OT audit for the salary expander
// -----------------------------------------------------------------------------
// Shows a row-per-day where the day contributed to OT math, with the context
// (Sunday / Holiday / Weekday) + the signed delta against 8h30m. Weekday short-
// time days (negative delta) are included so finance sees the full netting.
const OtBreakdownRow = ({ record }) => {
  const entries = useMemo(() => {
    const sunHoliday = record?.day_ot_hours || {};
    const weekdayDelta = record?.day_weekday_delta_min || {};
    const status = record?.day_status || {};
    const rate = Number(record?.ot_rate_per_hour || 0);

    const rows = [];
    // 1) Sunday / Holiday full-day OT rows (always positive).
    for (const [date, hours] of Object.entries(sunHoliday)) {
      const s = status[date] || "";
      const isSunday = ["paid_sunday", "unpaid_sunday", "pending_sunday"].includes(s);
      const isHoliday = s === "paid_holiday";
      if (!isSunday && !isHoliday) continue;         // weekdays handled below
      const kind = isSunday ? "Sunday" : "Holiday";
      const h = Number(hours);
      rows.push({
        date, kind,
        hours: h,
        amount: Math.round(h * rate),
      });
    }
    // 2) Weekday signed deltas (both positive and negative, since the net is
    //    what's paid).
    for (const [date, delta] of Object.entries(weekdayDelta)) {
      const h = Number(delta) / 60.0;
      rows.push({
        date, kind: "Weekday",
        hours: Number(h.toFixed(2)),
        amount: Math.round(h * rate),
      });
    }

    // Sort rows chronologically and attach pretty labels.
    rows.sort((a, b) => a.date.localeCompare(b.date));
    for (const r of rows) {
      const dt = new Date(r.date + "T00:00:00");
      const dowShort = dt.toLocaleDateString(undefined, { weekday: "short" });
      const dayNum = String(dt.getDate()).padStart(2, "0");
      const monShort = dt.toLocaleDateString(undefined, { month: "short" });
      r.label = `${dowShort} · ${dayNum} ${monShort}`;
    }

    // Totals: SUM Sunday/Holiday (full-day) + MAX(0, NET weekday deltas).
    const sunHoliday_h = rows
      .filter((r) => r.kind !== "Weekday")
      .reduce((acc, r) => acc + r.hours, 0);
    const weekdayNet_h = rows
      .filter((r) => r.kind === "Weekday")
      .reduce((acc, r) => acc + r.hours, 0);
    const paidWeekday_h = Math.max(0, weekdayNet_h);
    const totalHours = sunHoliday_h + paidWeekday_h;
    const totalAmount = Math.round(totalHours * rate);
    return { rows, totalHours, totalAmount, weekdayNet_h, paidWeekday_h, sunHoliday_h };
  }, [record]);

  if (entries.rows.length === 0) return null;

  const KIND_CLS = {
    Sunday:  "bg-emerald-100 text-emerald-800 border-emerald-300",
    Holiday: "bg-blue-100 text-blue-800 border-blue-300",
    Weekday: "bg-slate-100 text-slate-700 border-slate-300",
  };

  return (
    <div className="mt-3" data-testid={`ot-breakdown-${record.employee_id}`}>
      <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1">
        OT breakdown · {entries.rows.length} day{entries.rows.length === 1 ? "" : "s"}
      </p>
      <div className="inline-block border border-slate-200 rounded-sm bg-white">
        <table className="text-[11px]" data-testid={`ot-breakdown-table-${record.employee_id}`}>
          <thead className="bg-slate-50 text-[10px] tracking-wider uppercase text-slate-600">
            <tr>
              <th className="px-2 py-1 text-left font-semibold border-r border-slate-200">Date</th>
              <th className="px-2 py-1 text-left font-semibold border-r border-slate-200">Context</th>
              <th className="px-2 py-1 text-right font-semibold border-r border-slate-200">Δ vs 8h30m</th>
              <th className="px-2 py-1 text-right font-semibold">OT ₹</th>
            </tr>
          </thead>
          <tbody>
            {entries.rows.map((row) => {
              const neg = row.hours < 0;
              return (
                <tr key={`${row.date}-${row.kind}`} data-testid={`ot-breakdown-row-${row.date}`} className="border-t border-slate-100">
                  <td className="px-2 py-1 font-mono text-slate-800 border-r border-slate-200">{row.label}</td>
                  <td className="px-2 py-1 border-r border-slate-200">
                    <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold ${KIND_CLS[row.kind]}`}>{row.kind}</span>
                  </td>
                  <td className={`px-2 py-1 font-mono text-right border-r border-slate-200 ${neg ? "text-red-700" : "text-slate-900"}`}>
                    {row.hours > 0 ? "+" : ""}{row.hours.toFixed(2)}
                  </td>
                  <td className={`px-2 py-1 font-mono text-right ${neg ? "text-red-700" : "text-slate-900"}`}>
                    {neg ? "" : "₹"}{neg ? "—" : inr(row.amount)}
                  </td>
                </tr>
              );
            })}
            {/* Weekday-net subtotal — makes the "+8 − 5 = +3 min net" story explicit. */}
            {entries.rows.some((r) => r.kind === "Weekday") && (
              <tr className="border-t border-slate-200 bg-slate-50/60">
                <td className="px-2 py-1 text-slate-500 italic border-r border-slate-200" colSpan={2}>Weekday net (paid = max 0)</td>
                <td className={`px-2 py-1 font-mono text-right font-semibold border-r border-slate-200 ${entries.weekdayNet_h < 0 ? "text-red-700" : "text-slate-900"}`}>
                  {entries.weekdayNet_h > 0 ? "+" : ""}{entries.weekdayNet_h.toFixed(2)}
                </td>
                <td className="px-2 py-1 font-mono text-right font-semibold text-slate-900">
                  ₹{inr(Math.round(entries.paidWeekday_h * Number(record?.ot_rate_per_hour || 0)))}
                </td>
              </tr>
            )}
            <tr className="border-t-2 border-slate-300 bg-slate-50">
              <td className="px-2 py-1 font-semibold text-slate-700 border-r border-slate-200" colSpan={2}>Total OT paid</td>
              <td className="px-2 py-1 font-mono text-right font-bold text-slate-900 border-r border-slate-200">{entries.totalHours.toFixed(2)}</td>
              <td className="px-2 py-1 font-mono text-right font-bold text-slate-900">₹{inr(entries.totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};


// -----------------------------------------------------------------------------
// StatusChip — Attendance / Advance "uploaded?" indicator used in Salary tab
// -----------------------------------------------------------------------------
const StatusChip = ({ label, ok, okText = "Uploaded", missText = "Not uploaded", testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-sm border ${
      ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"
    }`}
    title={label}
  >
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
    <span className="text-slate-700">{label}</span>
    <span className={ok ? "text-emerald-800" : "text-red-800"}>· {ok ? okText : missText}</span>
  </span>
);


// -----------------------------------------------------------------------------
// SalarySlipsTab
// -----------------------------------------------------------------------------
const SalarySlipsTab = () => {
  const qc = useQueryClient();
  const [month, setMonth] = useState(monthOptions()[0]);

  // Status queries — do we have an attendance file + advance file for this month?
  // The salary table is BLURRED if attendance is missing.
  const { data: attendanceRecords = [], isLoading: attendanceLoading } = useQuery({
    queryKey: ["hr-attendance", month],
    queryFn: () => listAttendance({ month }),
  });
  const hasAttendance = attendanceRecords.length > 0;

  const { data: advanceUpload } = useQuery({
    queryKey: ["salary-advance-upload", month],
    queryFn: () => getSalaryAdvanceUpload(month),
  });
  const hasAdvance = !!(advanceUpload && advanceUpload.id);

  const { data: records = [], isLoading: recordsLoading } = useQuery({
    queryKey: ["hr-salary", month],
    queryFn: () => listSalaryRecords({ month }),
  });
  const genMut = useMutation({
    mutationFn: () => generateSalary({ month }),
    onSuccess: (data) => {
      toast.success(`Calculated salary for ${data.count} employees · ${month}`);
      qc.invalidateQueries({ queryKey: ["hr-salary"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Generation failed"),
  });

  // Auto-calculate the moment a month is selected: as soon as we see
  // attendance records for the month (and we're not already running), fire
  // generateSalary to pick up the latest attendance+advance. React-query's
  // cache keyed on month gives us "run exactly once per month-change".
  const autoFiredFor = useRef(new Set());
  useEffect(() => {
    if (attendanceLoading) return;
    if (!hasAttendance) return;                 // nothing to compute from
    if (genMut.isPending) return;
    if (autoFiredFor.current.has(month)) return;
    autoFiredFor.current.add(month);
    genMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, hasAttendance, attendanceLoading]);

  const [expanded, setExpanded] = useState(null);
  const isLoading = recordsLoading || genMut.isPending;

  return (
    <div data-testid="hr-salary-tab" className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-sm p-4">
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Month</label>
          <select data-testid="salary-month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-0.5 block h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono bg-white">
            {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Status chips — tell the user at a glance whether the two source files are in */}
        <div className="flex items-center gap-2 mt-4" data-testid="salary-upload-status">
          <StatusChip
            testId="status-attendance"
            label="Attendance Excel"
            ok={hasAttendance}
            okText="Uploaded"
            missText="Not uploaded"
          />
          <StatusChip
            testId="status-advance"
            label="Salary Advance"
            ok={hasAdvance}
            okText="Uploaded"
            missText="Not uploaded"
          />
        </div>

        <a
          href={salaryExportUrl(month)}
          target="_blank"
          rel="noreferrer"
          data-testid="download-salary-xlsx"
          className="h-10 mt-4 ml-auto inline-flex items-center gap-1.5 px-3 rounded-sm border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-semibold text-sm"
        >
          <FileSpreadsheet className="h-4 w-4" /> Download Excel
        </a>
      </div>

      {/* Table or blur-overlay */}
      <div className="relative border border-slate-200 rounded-sm bg-white overflow-x-auto">
        {/* No-attendance overlay: blur the table underneath and show a clear CTA */}
        {!hasAttendance && !attendanceLoading && (
          <div data-testid="no-attendance-overlay" className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <div className="text-center space-y-1 px-6 py-4 bg-white border border-amber-300 rounded-sm shadow-sm">
              <AlertCircle className="h-6 w-6 text-amber-700 mx-auto" />
              <p className="text-sm font-semibold text-slate-900">No attendance uploaded for {month}</p>
              <p className="text-xs text-slate-500">Upload the attendance Excel on the <b>Attendance & Salary Advance</b> tab first.</p>
            </div>
          </div>
        )}

        <div className={!hasAttendance ? "pointer-events-none select-none blur-[2px]" : ""}>
          {isLoading ? <div className="p-4"><Loader2 className="h-5 w-5 animate-spin" /></div> : records.length === 0 ? (
            <p className="p-8 text-sm text-slate-500 text-center">
              {hasAttendance ? `Calculating ${month}…` : `No salary computed for ${month}.`}
            </p>
          ) : (
            <table className="min-w-full text-xs border-collapse">
              <thead>
              <tr className="text-[10px] tracking-[0.2em] uppercase text-white">
                <th colSpan={5} className="px-3 py-2 bg-[#1E3A8A] border-b border-slate-200 text-center">Employee Master</th>
                <th colSpan={6} className="px-3 py-2 bg-[#1D4ED8] border-b border-slate-200 text-center">Attendance</th>
                <th colSpan={4} className="px-3 py-2 bg-[#16A34A] border-b border-slate-200 text-center">Earnings</th>
                <th colSpan={5} className="px-3 py-2 bg-[#B45309] border-b border-slate-200 text-center">Deductions &amp; Net Pay</th>
              </tr>
              <tr className="bg-slate-900 text-white text-[10px] tracking-[0.15em] uppercase">
                <th className="px-2 py-2 text-center">Sl. No.</th>
                <th className="px-2 py-2 text-left">Emp Code</th>
                <th className="px-2 py-2 text-left">Employee Name</th>
                <th className="px-2 py-2 text-left">Department</th>
                <th className="px-2 py-2 text-left">Skill Grade</th>
                <th className="px-2 py-2 text-right">Days in Month</th>
                <th className="px-2 py-2 text-right">LWP Days (absent)</th>
                <th className="px-2 py-2 text-right">Sandwich Sundays</th>
                <th className="px-2 py-2 text-right">Total Unpaid Days</th>
                <th className="px-2 py-2 text-right">Paid Days</th>
                <th className="px-2 py-2 text-right">OT Hours</th>
                <th className="px-2 py-2 text-right">Monthly Salary (Gross)</th>
                <th className="px-2 py-2 text-center">Eligible for OT</th>
                <th className="px-2 py-2 text-right">OT Rate / Hour</th>
                <th className="px-2 py-2 text-right">OT Amount</th>
                <th className="px-2 py-2 text-right">Gross Earnings</th>
                <th className="px-2 py-2 text-right">Advance Recovery</th>
                <th className="px-2 py-2 text-right">Total Deductions</th>
                <th className="px-2 py-2 text-right">Net Payable BEFORE ESI / PF</th>
                <th className="px-2 py-2 text-center">ESI Applicable</th>
                <th className="px-2 py-2 text-center">PF Applicable</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, idx) => (
                <Fragment key={r.employee_id}>
                <tr data-testid={`salary-row-${r.employee_id}`} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${idx % 2 === 1 ? "bg-amber-50/40" : ""}`} onClick={() => setExpanded(expanded === r.employee_id ? null : r.employee_id)}>
                  <td className="px-2 py-1.5 text-center font-mono">{idx + 1}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px] text-slate-700">{r.emp_code || "—"}</td>
                  <td className="px-2 py-1.5 font-semibold text-slate-900">{r.employee_name}</td>
                  <td className="px-2 py-1.5 text-slate-700">{r.department || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-700">{r.skill_grade || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.days_in_month}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${Number(r.lwp_days) > 0 ? "text-red-700 font-bold" : "text-slate-500"}`}>{r.lwp_days ?? 0}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${Number(r.sandwich_sundays) > 0 ? "text-red-700 font-bold" : "text-slate-500"}`}>{r.sandwich_sundays ?? 0}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${Number(r.total_unpaid_days) > 0 ? "text-red-700 font-bold" : "text-slate-500"}`}>{r.total_unpaid_days ?? 0}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">{r.paid_days ?? r.paid_days_equiv ?? 0}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    <div className="inline-flex items-center justify-end gap-1">
                      <span>{r.ot_hours || 0}</span>
                      {Number(r.weekday_ot_net_minutes) < 0 && (
                        <span
                          title={`Weekday net was ${r.weekday_ot_net_minutes} min — short-time month, OT paid = 0`}
                          data-testid={`short-time-chip-${r.employee_id}`}
                          className="px-1 py-0.5 rounded-sm border border-orange-300 bg-orange-50 text-orange-800 text-[9px] font-semibold tracking-wide uppercase"
                        >
                          Short {r.weekday_ot_net_minutes}m
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">₹{inr(r.monthly_basic)}</td>
                  <td className="px-2 py-1.5 text-center font-mono text-[10px]">
                    <span className={r.ot_eligible ? "text-emerald-700 font-bold" : "text-slate-400"}>{r.ot_eligible ? "Y" : "N"}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.ot_rate_per_hour ? `₹${inr(r.ot_rate_per_hour)}` : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{Number(r.ot_amount) > 0 ? `₹${inr(r.ot_amount)}` : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-800">₹{inr(r.gross_earnings ?? (Number(r.gross_basic_earned || 0) + Number(r.ot_amount || 0)))}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    <span className={Number(r.advance_total) > 0 ? "text-amber-800" : "text-slate-400"} data-testid={`advance-amt-${r.employee_id}`}
                          title={Number(r.advance_total) > 0 ? "From the Salary Advance Excel uploaded this month" : "No advance uploaded for this month"}>
                      {Number(r.advance_total) > 0 ? `−₹${inr(r.advance_total)}` : "—"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{Number(r.total_deductions) > 0 ? `₹${inr(r.total_deductions)}` : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-900">₹{inr(r.net_payable_before_esi_pf ?? r.net_salary)}</td>
                  <td className="px-2 py-1.5 text-center font-mono text-[10px]">
                    <span className={r.esi_eligible ? "text-emerald-700 font-bold" : "text-slate-400"}>{r.esi_eligible ? "Yes" : "No"}</span>
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono text-[10px]">
                    <span className={r.pf_eligible ? "text-emerald-700 font-bold" : "text-slate-400"}>{r.pf_eligible ? "Yes" : "No"}</span>
                  </td>
                </tr>
                {expanded === r.employee_id && (
                  <tr>
                    <td colSpan={21} className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                      <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1">Day-by-day status</p>
                      <div className="flex flex-wrap gap-1 text-[10px]">
                        {Object.entries(r.day_status || {}).sort().map(([d, s]) => (
                          <span key={d} className={`px-1.5 py-0.5 rounded-sm font-mono ${
                            s === "present" ? "bg-green-100 text-green-800" :
                            s === "absent" ? "bg-red-100 text-red-800" :
                            s === "half_day" ? "bg-orange-100 text-orange-800" :
                            s === "paid_holiday" ? "bg-blue-100 text-blue-800" :
                            s === "paid_sunday" ? "bg-emerald-100 text-emerald-800" :
                            "bg-slate-100 text-slate-700"
                          }`} title={s}>
                            {d.slice(-2)} {s === "absent" ? "✕" : s === "half_day" ? "½" : s === "paid_holiday" ? "H" : s === "paid_sunday" ? "S" : s === "unpaid_sunday" ? "s" : "✓"}
                            {r.day_ot_hours?.[d] ? ` +${r.day_ot_hours[d]}h` : ""}
                          </span>
                        ))}
                      </div>

                      {/* Per-day OT audit — tiny breakdown showing WHICH Sunday / Holiday / Weekday contributed how many OT hours. */}
                      <OtBreakdownRow record={r} />

                      <p className="mt-2 text-[11px] text-slate-500">Days in month: {r.days_in_month} · Daily rate: ₹{inr(r.daily_rate)} · Hourly: ₹{inr(r.hourly_rate)} · Half days rolled into LWP as 0.5 each</p>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Main page

// -----------------------------------------------------------------------------
// LeaveRequestsTab
// -----------------------------------------------------------------------------
const LeaveRequestsTab = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [scope, setScope] = useState("pending_l1");
  const isDirector = user?.role === "director";
  const { data, isLoading } = useQuery({
    queryKey: ["leaves", scope],
    queryFn: () => listLeaves(scope),
    refetchInterval: 30_000,
  });
  const mut = useMutation({
    mutationFn: ({ lrId, action }) => leaveAction(lrId, action),
    onSuccess: () => {
      toast.success("Action completed");
      qc.invalidateQueries({ queryKey: ["leaves"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const rows = data?.leaves || [];
  const scopes = [
    { key: "pending_l1", label: "Pending L1" },
    { key: "pending_l2", label: "Pending Director" },
    { key: "all", label: "All requests" },
  ];
  const badge = (s) => ({
    PENDING_L1: "bg-amber-50 border-amber-200 text-amber-900",
    PENDING_L2: "bg-blue-50 border-blue-200 text-blue-900",
    APPROVED: "bg-emerald-50 border-emerald-200 text-emerald-900",
    REJECTED: "bg-red-50 border-red-200 text-red-900",
    CANCELLED: "bg-slate-50 border-slate-200 text-slate-600",
    AUTO_HALF_DAY: "bg-orange-50 border-orange-200 text-orange-900",
  }[s] || "bg-slate-50 border-slate-200 text-slate-700");

  return (
    <section data-testid="hr-leave-tab" className="space-y-3">
      <div className="flex gap-2 flex-wrap items-center">
        {scopes.map((s) => (
          <button
            key={s.key}
            onClick={() => setScope(s.key)}
            data-testid={`leave-scope-${s.key}`}
            className={`px-3 py-1 text-xs font-semibold border rounded-sm ${
              scope === s.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {s.label}
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-2">{isLoading ? "…" : `${rows.length} row(s)`}</span>
      </div>
      {rows.length === 0 && !isLoading && (
        <p className="text-slate-500 italic text-sm py-6">No leave requests in this view.</p>
      )}
      {rows.length > 0 && (
        <div className="border border-slate-200 rounded-sm bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="leave-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] tracking-widest uppercase font-semibold text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">LR</th>
                <th className="text-left px-3 py-2">Employee</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">From → To</th>
                <th className="text-right px-3 py-2">Days</th>
                <th className="text-left px-3 py-2">Supervisor</th>
                <th className="text-left px-3 py-2">Applied by</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`leave-row-${r.lr_id}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-[11px] font-semibold">{r.lr_id}</td>
                  <td className="px-3 py-2">{r.employee_name} <span className="text-[10px] text-slate-500 font-mono ml-1">{r.employee_id}</span></td>
                  <td className="px-3 py-2 text-[11px]">
                    {r.leave_type.replace(/_/g, " ")}
                    {r.is_late && <span className="ml-1 inline-block text-[9px] font-bold text-red-700 bg-red-50 border border-red-200 px-1 rounded-sm">LATE</span>}
                    {r.conversion_note && <div className="text-[10px] text-orange-800 mt-0.5" title={r.conversion_note}>⚠ auto-converted</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.from_date}{r.to_date !== r.from_date && ` → ${r.to_date}`}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.days}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-600">{r.supervisor_id}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-600">{r.applied_by_name}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-sm border ${badge(r.status)}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-600 max-w-[240px] truncate" title={r.reason}>{r.reason}</td>
                  <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                    {(r.status === "PENDING_L1" || r.status === "AUTO_HALF_DAY") && (
                      <>
                        <button data-testid={`l1-approve-${r.lr_id}`} onClick={() => mut.mutate({ lrId: r.lr_id, action: "l1-approve" })} className="text-emerald-700 hover:text-emerald-900" title="L1 Approve"><CheckCircle2 className="h-4 w-4 inline" /></button>
                        <button data-testid={`l1-reject-${r.lr_id}`} onClick={() => mut.mutate({ lrId: r.lr_id, action: "l1-reject" })} className="text-red-700 hover:text-red-900" title="L1 Reject"><XCircle className="h-4 w-4 inline" /></button>
                      </>
                    )}
                    {r.status === "PENDING_L2" && isDirector && (
                      <>
                        <button data-testid={`l2-approve-${r.lr_id}`} onClick={() => mut.mutate({ lrId: r.lr_id, action: "l2-approve" })} className="text-emerald-700 hover:text-emerald-900" title="Director Approve"><CheckCircle2 className="h-4 w-4 inline" /></button>
                        <button data-testid={`l2-reject-${r.lr_id}`} onClick={() => mut.mutate({ lrId: r.lr_id, action: "l2-reject" })} className="text-red-700 hover:text-red-900" title="Director Reject"><XCircle className="h-4 w-4 inline" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

// -----------------------------------------------------------------------------
const HR = () => {
  const { hasModule } = useAuth();
  const visibleHrTabs = useMemo(
    () => TABS.filter((t) => hasModule(t.module)),
    [hasModule],
  );
  const [tab, setTab] = useState(visibleHrTabs[0]?.key || "employees");
  useEffect(() => {
    if (visibleHrTabs.length && !visibleHrTabs.some((t) => t.key === tab)) {
      setTab(visibleHrTabs[0].key);
    }
  }, [visibleHrTabs, tab]);
  return (
    <div data-testid="hr-page" className="space-y-5">
      <div className="border-b border-slate-200 pb-3">
        <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">People &amp; Payroll</p>
        <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Human Resources</h2>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {visibleHrTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`hr-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold tracking-wide uppercase border-b-2 ${
                tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "employees" && <EmployeesTab />}
      {tab === "attendance" && <AttendanceTab />}
      {tab === "leave" && <LeaveRequestsTab />}
      {tab === "holidays" && <HolidaysTab />}
      {tab === "rules" && <SalaryRulesTab />}
      {tab === "salary" && <SalarySlipsTab />}
      {tab === "performance" && <Performance />}
      {tab === "talent" && <TalentPipeline />}
    </div>
  );
};

export default HR;
